import { useCallback, useLayoutEffect, useState } from 'react';

function getDimensionObject(node: HTMLElement) {
  const rect = node.getBoundingClientRect();

  return {
    width: rect.width,
    height: rect.height,
    top: rect.top,
    left: rect.left,
    x: rect.x,
    y: rect.y,
    right: rect.right,
    bottom: rect.bottom
  };
}

export type MeasuredDimensions = ReturnType<typeof getDimensionObject>;

function useDimensions({ liveMeasure = true } = {}) {
  const [dimensions, setDimensions] = useState<Partial<MeasuredDimensions>>({});
  const [node, setNode] = useState<HTMLElement | null>(null);

  const ref = useCallback((newNode: HTMLElement | null) => {
    setNode(newNode);
  }, []);

  useLayoutEffect(() => {
    if (!node) {
      return undefined;
    }

    const measure = () =>
      window.requestAnimationFrame(() => {
        setDimensions(getDimensionObject(node));
      });
    measure();

    if (!liveMeasure) {
      return undefined;
    }

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure);
    const ro = new ResizeObserver(measure);
    if (node.parentElement) {
      ro.observe(node.parentElement);
    }

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
      ro.disconnect();
    };
  }, [node, liveMeasure]);

  return [ref, dimensions, node] as const;
}

export default useDimensions;
